import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { IMG, GALLERY } from "@/data/site";

const cats = ["All", "Hotel", "Rooms", "Dining", "Facilities"];

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const items = cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === cat);

  return (
    <div data-testid="gallery-page">
      <PageHero overline="Gallery" title="Moments in gold" subtitle="A visual journey through the hotel, its rooms, plates and celebrations." image={IMG.aerialView} />
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap gap-3 mb-14">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-5 py-2.5 text-[12px] uppercase tracking-[0.18em] border transition-colors duration-300 ${cat === c ? "bg-[#d4af37] text-black border-[#d4af37]" : "border-white/15 text-white/60 hover:border-[#d4af37]/50 hover:text-white"}`}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {items.map((g) => (
              <motion.button
                layout
                key={g.src + g.label}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setLightbox(g)}
                className="group relative img-zoom aspect-[4/3] text-left"
                data-testid={`gallery-item-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <img src={g.src} alt={g.label} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                <p className="absolute bottom-4 left-5 font-serif-display text-lg text-white opacity-0 group-hover:opacity-100 transition-opacity duration-400">{g.label}</p>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-white/70 hover:text-[#d4af37] transition-colors duration-300" data-testid="lightbox-close-btn">
              <X size={30} />
            </button>
            <motion.figure initial={{ scale: 0.92 }} animate={{ scale: 1 }} className="max-w-5xl w-full">
              <img src={lightbox.src} alt={lightbox.label} className="w-full max-h-[80vh] object-contain" />
              <figcaption className="mt-4 text-center font-serif-display text-xl text-[#d4af37]">{lightbox.label}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
